<template>
  <div>
    <div class="min-h-screen flex-1 bg-gray-100 px-3 items-center">
      <div class="bg-white w-full rounded-lg shadow overflow-hidden">
        <!-- Header -->
        <div class="h-20 py-3 flex items-center justify-between border-gray-200 p-6">
          <div class="flex items-center">
            <div class="text-2xl font-bold text-green-700">
              <i class="material-icons text-green-600 align-middle mr-2">group</i>
              {{ $t('team_management') || 'Team Management' }}
            </div>
          </div>
          <div class="flex items-center space-x-4">
            <!-- Stats Cards -->
            <div class="flex space-x-3">
              <div class="bg-green-50 px-4 py-2 rounded-lg">
                <span class="text-sm text-gray-600">{{ $t('total') || 'Total' }}</span>
                <span class="ml-2 font-bold text-green-700">{{ stats.total || 0 }}</span>
              </div>
              <div class="bg-blue-50 px-4 py-2 rounded-lg">
                <span class="text-sm text-gray-600">{{ $t('active') || 'Active' }}</span>
                <span class="ml-2 font-bold text-blue-700">{{ (stats.byStatus && stats.byStatus.active) || 0 }}</span>
              </div>
            </div>
            <!-- Add Member Button -->
            <router-link
              v-if="canManageTeam && canCreate"
              to="/seller-team/new"
              class="flex items-center rounded focus:outline-none text-white px-4 py-2 bg-green-600 hover:bg-green-700 transition-all duration-300"
            >
              <i class="material-icons mr-1">person_add</i>
              {{ $t('add_member') || 'Add Member' }}
            </router-link>
          </div>
        </div>

        <!-- Team Members Table -->
        <div class="overflow-x-auto p-4">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('member') || 'Member' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('contact') || 'Contact' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('role') || 'Role' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('status') || 'Status' }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('last_activity') || 'Last Activity' }}
                </th>
                <th v-if="canManageTeam && (canUpdate || canDelete)" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {{ $t('actions') || 'Actions' }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="member in teamMembers" :key="member._id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <div class="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span class="text-green-700 font-medium text-sm">
                          {{ getInitials(member.fullName) }}
                        </span>
                      </div>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">{{ member.fullName }}</div>
                      <div class="text-sm text-gray-500">{{ formatDate(member.createdAt) }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ member.email }}</div>
                  <div class="text-sm text-gray-500">{{ member.phone }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getRoleBadgeClass(member.teamRole)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ member.teamRole }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="getStatusBadgeClass(member.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                    {{ member.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ member.last_activity ? formatDate(member.last_activity) : 'Never' }}
                </td>
                <td v-if="canManageTeam && (canUpdate || canDelete)" class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex justify-end space-x-2">
                    <router-link v-if="canUpdate" :to="'/seller-team/edit/' + member._id" class="text-blue-600 hover:text-blue-900" title="Edit">
                      <i class="material-icons text-sm">edit</i>
                    </router-link>
                    <button v-if="canUpdate" @click="resendInvite(member)" class="text-green-600 hover:text-green-900" title="Resend Invitation">
                      <i class="material-icons text-sm">email</i>
                    </button>
                    <button v-if="canDelete" @click="confirmRemove(member)" class="text-red-600 hover:text-red-900" title="Remove">
                      <i class="material-icons text-sm">delete</i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="teamMembers.length === 0">
                <td :colspan="(canManageTeam && (canUpdate || canDelete)) ? 6 : 5" class="px-6 py-12 text-center text-gray-500">
                  <i class="material-icons text-4xl text-gray-300 mb-2">group_off</i>
                  <p>{{ $t('no_team_members') || 'No team members yet' }}</p>
                  <router-link v-if="canManageTeam && canCreate" to="/seller-team/new" class="mt-3 text-green-600 hover:text-green-800 block">
                    {{ $t('add_first_member') || 'Add your first team member' }}
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="paginate.lastpage > 1" class="p-4 flex justify-center">
          <div class="flex items-center space-x-2">
            <button
              @click="prevPage"
              :disabled="paginate.currentpage === 1"
              class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              <i class="material-icons text-sm">chevron_left</i>
            </button>
            <span class="text-sm text-gray-600">
              {{ paginate.currentpage }} / {{ paginate.lastpage }}
            </span>
            <button
              @click="nextPage"
              :disabled="paginate.currentpage === paginate.lastpage"
              class="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              <i class="material-icons text-sm">chevron_right</i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Delete Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showDeleteModal = false"></div>
        <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div class="flex items-center mb-4">
            <div class="flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
              <i class="material-icons text-red-600">warning</i>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">{{ $t('remove_member') || 'Remove Team Member' }}</h3>
            </div>
          </div>
          <p class="text-sm text-gray-500 mb-4">
            {{ $t('confirm_remove_member') || 'Are you sure you want to remove' }} <strong>{{ memberToDelete ? memberToDelete.fullName : '' }}</strong>?
            {{ $t('action_cannot_undone') || 'This action cannot be undone.' }}
          </p>
          <div class="flex justify-end space-x-3">
            <button
              @click="showDeleteModal = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {{ $t('cancel') || 'Cancel' }}
            </button>
            <button
              @click="removeMember"
              :disabled="loading"
              class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {{ $t('remove') || 'Remove' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SellerTeam',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      teamMembers: [],
      stats: {
        total: 0,
        byStatus: {},
        byRole: {}
      },
      paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 1,
        per_page: 50,
      },
      showDeleteModal: false,
      loading: false,
      memberToDelete: null,
    };
  },
  computed: {
    canManageTeam() {
      // Owner can always manage team, team members need permission
      if (!this.currentUser) return false;
      if (!this.currentUser.parentSeller) return true; // Owner
      return (this.currentUser.teamPermissions && this.currentUser.teamPermissions.canManageTeam) || false;
    },
    canCreate() {
      return this.getAccess('sellerTeam', 'create');
    },
    canRead() {
      return this.getAccess('sellerTeam', 'read');
    },
    canUpdate() {
      return this.getAccess('sellerTeam', 'update');
    },
    canDelete() {
      return this.getAccess('sellerTeam', 'delete');
    }
  },
  async mounted() {
    await this.loadTeamMembers();
    await this.loadStats();
  },
  methods: {
    async loadTeamMembers() {
      const res = await this.$server.getTeamMembers('sellerTeam', {
        page: this.paginate.currentpage,
        limit: this.paginate.per_page
      });
      if (res && res.content) {
        this.teamMembers = res.content.results || [];
        this.paginate.total = res.content.total || 0;
        this.paginate.lastpage = res.content.last_page || 1;
        this.paginate.currentpage = res.content.current_page || 1;
      }
    },
    async loadStats() {
      const res = await this.$server.getTeamStats('sellerTeam', {});
      if (res && res.content) {
        this.stats = res.content;
      }
    },
    confirmRemove(member) {
      this.memberToDelete = member;
      this.showDeleteModal = true;
    },
    async removeMember() {
      this.loading = true;
      try {
        const res = await this.$server.removeTeamMember('sellerTeam', { _id: this.memberToDelete._id });
        if (res && res.content) {
          alert('Member removed successfully', 'success');
          this.showDeleteModal = false;
          this.memberToDelete = null;
          await this.loadTeamMembers();
          await this.loadStats();
        }
      } catch (error) {
        console.error('Error removing member:', error);
      } finally {
        this.loading = false;
      }
    },
    async resendInvite(member) {
      try {
        const res = await this.$server.resendInvitation('sellerTeam', { _id: member._id });
        if (res && res.content) {
          alert('Invitation email sent successfully', 'success');
        }
      } catch (error) {
        console.error('Error sending invitation:', error);
      }
    },
    async nextPage() {
      if (this.paginate.currentpage < this.paginate.lastpage) {
        this.paginate.currentpage++;
        await this.loadTeamMembers();
      }
    },
    async prevPage() {
      if (this.paginate.currentpage > 1) {
        this.paginate.currentpage--;
        await this.loadTeamMembers();
      }
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    getRoleBadgeClass(role) {
      const classes = {
        owner: 'bg-purple-100 text-purple-800',
        manager: 'bg-blue-100 text-blue-800',
        operator: 'bg-green-100 text-green-800',
        viewer: 'bg-gray-100 text-gray-800'
      };
      return classes[role] || 'bg-gray-100 text-gray-800';
    },
    getStatusBadgeClass(status) {
      const classes = {
        active: 'bg-green-100 text-green-800',
        inactive: 'bg-yellow-100 text-yellow-800',
        blocked: 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },
    getAccess(modelToCheck, accessToCheck) {
      if (!this.currentUser || !this.currentUser.role || !this.currentUser.role.permissions) return false;
      const permissions = this.currentUser.role.permissions;
      for (let i in permissions) {
        let model = permissions[i]['model'];
        let access = permissions[i]['access'];
        if (model === modelToCheck) {
          return access[accessToCheck] || false;
        }
      }
      return false;
    }
  }
};
</script>

<style scoped>
.material-icons {
  font-size: 20px;
}
</style>
